import { useMediaQuery } from '@/hooks/useMediaQuery';

/**
 * The one place the right slot's responsive thresholds are defined.
 *
 * They used to be spelled out independently in four files, and two of them were
 * a hand-coordinated pair: AppLayout asked for `(min-width: 1024px)` to decide
 * whether to render the split panel, and GlobalRightDock separately asked for
 * `(max-width: 1023px)` to decide whether to render a Sheet instead. Nothing
 * derived the 1023 from the 1024 — they agreed only because someone typed both,
 * so moving one would silently open a window where the layout wants a split
 * panel and the panel renders a full-screen Sheet inside it.
 *
 * Deriving the compact case as "not split" makes that class of drift impossible.
 */

/** Below this, a resizable right pane cannot share the viewport with the main column. */
export const RIGHT_SLOT_SPLIT_MIN_PX = 1024;

/** The workflow drawer is narrower, so it can share the viewport sooner. */
export const RIGHT_SLOT_MEDIUM_MIN_PX = 768;

export const RIGHT_SLOT_SPLIT_QUERY = `(min-width: ${RIGHT_SLOT_SPLIT_MIN_PX}px)`;
export const RIGHT_SLOT_MEDIUM_QUERY = `(min-width: ${RIGHT_SLOT_MEDIUM_MIN_PX}px)`;

/** True when the viewport is wide enough for a full-size right pane beside the main column. */
export function useCanSplitRightSlot(): boolean {
  return useMediaQuery(RIGHT_SLOT_SPLIT_QUERY);
}

/** True when the viewport is wide enough for the narrower workflow drawer. */
export function useCanSplitRightSlotMedium(): boolean {
  return useMediaQuery(RIGHT_SLOT_MEDIUM_QUERY);
}

/**
 * True when a dock panel must present as a full-screen Sheet instead of a pane.
 * Defined as the exact complement of the split case — never as its own threshold.
 */
export function useIsCompactRightSlot(): boolean {
  return !useCanSplitRightSlot();
}
